import { Component } from '@theme/component';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Floating product video (sections/sm-product-reel.liquid).
 *
 * A small muted, looping card pinned to the side of the product page. Tapping it opens
 * a <dialog> player with sound; with several videos the card cycles through them and the
 * player gets previous/next. Closing the card leaves a small play button to bring it back.
 * Nothing is downloaded until the page has loaded and the card is shown.
 *
 * @typedef {{ id?: number, preview: string, full: string, poster: string }} Reel
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} data - JSON list of reels.
 * @property {HTMLElement} card - The floating card.
 * @property {HTMLVideoElement} preview - Muted card video.
 * @property {HTMLElement} [previewDots] - Card position dots.
 * @property {HTMLButtonElement} [reopen] - Optional small play button shown after hiding.
 * @property {HTMLDialogElement} dialog - Player dialog.
 * @property {HTMLVideoElement} player - Player video.
 * @property {HTMLButtonElement} sound - Mute toggle.
 * @property {HTMLButtonElement} prev - Previous video.
 * @property {HTMLButtonElement} next - Next video.
 *
 * @extends {Component<Refs>}
 */
class SmProductReel extends Component {
  requiredRefs = ['data', 'card', 'preview', 'dialog', 'player', 'sound', 'prev', 'next'];

  static STORAGE_KEY = 'sm-reel-hidden';

  /** Closing hides the reel on this product only. */
  get #storageKey() {
    return `${SmProductReel.STORAGE_KEY}:${this.dataset.productId || location.pathname}`;
  }

  /** @type {Reel[]} */
  #reels = [];
  #index = 0;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {HTMLElement | null} */
  #opener = null;
  #playerOpen = false;
  /** Phones: true while the product photos are on screen (the card waits below them). */
  #overMedia = false;
  #phone = window.matchMedia('(max-width: 767px)');
  /** @type {Element | null} */
  #media = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#reels = JSON.parse(this.refs.data.textContent || '[]');
    } catch {
      this.#reels = [];
    }
    if (!this.#reels.length) return;

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // Card: several videos play one after another; a single one loops.
    this.refs.preview.loop = this.#reels.length === 1;
    this.refs.preview.addEventListener('ended', () => this.#showPreview(this.#index + 1), { signal });

    // Player: Esc goes through closePlayer(); 'close' is a backup (cleanup is idempotent).
    this.refs.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.closePlayer();
      },
      { signal }
    );
    this.refs.dialog.addEventListener('close', () => this.#afterClose(), { signal });
    this.refs.player.addEventListener('volumechange', () => this.#syncSound(), { signal });

    // Pause the card while the tab is hidden.
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.refs.preview.pause() : this.#playPreview()),
      { signal }
    );

    const start = () => this.#start();
    this.#watchMedia();
    this.#phone.addEventListener('change', () => this.#applyAway(), { signal });

    const later = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 300));
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.refs.preview?.pause();
    this.refs.player?.pause();
  }

  /** Phones: keep the reel out of the way until the shopper scrolls past the photos. */
  #watchMedia() {
    if (this.dataset.afterMedia !== 'true') return;
    this.#media = document.querySelector('.product-information media-gallery, .product-information__media');
    if (!this.#media || !this.#listeners) return;
    const { signal } = this.#listeners;
    // One rect read per scroll event; only acts when the state flips.
    const check = () => {
      const over = (this.#media?.getBoundingClientRect().bottom ?? 0) > 0;
      if (over === this.#overMedia) return;
      this.#overMedia = over;
      this.#applyAway();
    };
    // Phones scroll the window; Horizon scrolls .page-wrapper on desktop.
    window.addEventListener('scroll', check, { passive: true, signal });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', check, { passive: true, signal });
    window.addEventListener('resize', check, { passive: true, signal });
    this.#overMedia = (this.#media.getBoundingClientRect().bottom ?? 0) > 0;
    this.#applyAway();
  }

  #applyAway() {
    const away = this.#overMedia && this.#phone.matches;
    this.classList.toggle('is-away', away);
    if (away) this.refs.preview.pause();
    else this.#playPreview();
  }

  async #start() {
    await this.#useLighterSources();
    if (this.#isRemembered()) {
      if (this.refs.reopen) this.refs.reopen.hidden = false;
      return;
    }
    this.show();
  }

  /**
   * Liquid only exposes the 1080p MP4. The product JSON lists every rendition, so the
   * card uses ~480p and the player ~720p (much lighter on phones). Failures keep 1080p.
   */
  async #useLighterSources() {
    const url = this.dataset.productJson;
    if (!url || !this.#reels.some((reel) => reel.id)) return;
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      /** @type {{ media?: Array<{ id: number, sources?: Array<{ format: string, url: string, width: number, height: number }> }> }} */
      const product = await response.json();
      for (const reel of this.#reels) {
        const media = product.media?.find((item) => item.id === reel.id);
        const mp4 = (media?.sources ?? [])
          .filter((source) => source.format === 'mp4')
          .sort((a, b) => Math.min(a.width, a.height) - Math.min(b.width, b.height));
        if (!mp4.length) continue;
        const short = (/** @type {{ width: number, height: number }} */ source) => Math.min(source.width, source.height);
        reel.preview = mp4[0].url;
        reel.full = (mp4.filter((source) => short(source) <= 720).pop() ?? mp4[0]).url;
      }
    } catch {
      // Keep the HD sources from Liquid.
    }
  }

  /** Shows the floating card (from the reopen button, or on load). */
  show() {
    this.refs.card.hidden = false;
    if (this.refs.reopen) this.refs.reopen.hidden = true;
    this.#remember(false);
    this.#renderDots();
    this.#showPreview(this.#index);
  }

  /** Hides the card and leaves the small play button. */
  hide() {
    this.refs.preview.pause();
    this.refs.card.hidden = true;
    this.#remember(true);
    if (this.refs.reopen) {
      this.refs.reopen.hidden = false;
      this.refs.reopen.focus({ preventScroll: true });
    }
  }

  /** @param {Event} [event] */
  openPlayer(event) {
    // Framework events are delegated, so find the actual button that was pressed.
    const target = event?.target instanceof Element ? event.target.closest('button') : null;
    this.#opener = target instanceof HTMLElement ? target : this.querySelector('.sm-reel-float__open');
    this.refs.preview.pause();
    this.refs.dialog.showModal();
    this.#playerOpen = true;
    document.documentElement.classList.add('sm-reel-open');
    this.#loadPlayer(this.#index);
  }

  closePlayer() {
    if (this.refs.dialog.open) this.refs.dialog.close();
    this.#afterClose();
  }

  previous() {
    this.#loadPlayer(this.#index - 1);
  }

  next() {
    this.#loadPlayer(this.#index + 1);
  }

  togglePlay() {
    const { player } = this.refs;
    if (player.paused) player.play().catch(() => {});
    else player.pause();
  }

  toggleSound() {
    this.refs.player.muted = !this.refs.player.muted;
  }

  #afterClose() {
    if (!this.#playerOpen) return;
    this.#playerOpen = false;
    this.refs.player.onended = null;
    this.refs.player.pause();
    this.refs.player.removeAttribute('src');
    this.refs.player.load();
    document.documentElement.classList.remove('sm-reel-open');
    if (!this.refs.card.hidden) this.#showPreview(this.#index);
    this.#opener?.focus({ preventScroll: true });
  }

  /** @param {number} index */
  #showPreview(index) {
    this.#index = (index + this.#reels.length) % this.#reels.length;
    const reel = this.#reels[this.#index];
    const { preview } = this.refs;
    if (!reel) return;

    if (preview.dataset.src !== reel.preview) {
      preview.dataset.src = reel.preview;
      if (reel.poster) preview.poster = reel.poster;
      if (!reducedMotion.matches) preview.src = reel.preview;
    }
    this.#updateDots();
    this.#playPreview();
  }

  #playPreview() {
    if (this.refs.card.hidden || this.refs.dialog.open || document.hidden) return;
    if (this.classList.contains('is-away')) return;
    if (reducedMotion.matches) return; // poster only; the player still plays on request
    this.refs.preview.play().catch(() => {}); // muted autoplay; ignore if the browser declines
  }

  /** @param {number} index */
  #loadPlayer(index) {
    this.#index = (index + this.#reels.length) % this.#reels.length;
    const reel = this.#reels[this.#index];
    const { player, prev, next } = this.refs;
    if (!reel) return;

    const many = this.#reels.length > 1;
    prev.hidden = !many;
    next.hidden = !many;

    player.poster = reel.poster || '';
    player.src = reel.full;
    player.loop = !many;
    player.muted = false;
    player.play().catch(() => {
      // Some browsers refuse sound without a fresh gesture: fall back to muted playback.
      player.muted = true;
      player.play().catch(() => {});
    });
    player.onended = many ? () => this.#loadPlayer(this.#index + 1) : null;
    this.#syncSound();
    this.#updateDots();
  }

  #syncSound() {
    const { player, sound } = this.refs;
    sound.classList.toggle('is-muted', player.muted);
    sound.setAttribute('aria-label', (player.muted ? sound.dataset.labelUnmute : sound.dataset.labelMute) || '');
  }

  #renderDots() {
    const dots = this.refs.previewDots;
    if (!dots || this.#reels.length < 2 || dots.childElementCount) return;
    dots.replaceChildren(...this.#reels.map(() => document.createElement('span')));
  }

  #updateDots() {
    const dots = this.refs.previewDots;
    if (!dots) return;
    [...dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === this.#index));
  }

  #isRemembered() {
    if (this.dataset.remember !== 'true') return false;
    try {
      return sessionStorage.getItem(this.#storageKey) === '1';
    } catch {
      return false;
    }
  }

  /** @param {boolean} hidden */
  #remember(hidden) {
    if (this.dataset.remember !== 'true') return;
    try {
      if (hidden) sessionStorage.setItem(this.#storageKey, '1');
      else sessionStorage.removeItem(this.#storageKey);
    } catch {
      // Storage unavailable (private mode): the card simply shows again next page.
    }
  }
}

if (!customElements.get('sm-product-reel')) {
  customElements.define('sm-product-reel', SmProductReel);
}
